import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSMLRouter } from '../../routing/sml/hooks';
import { SMLRoutes } from '../../routing/sml/SMLRoutes';
import { useTourActive, useUserTokenDecoded } from '../../redux/slices/appInfo/hooks';
import { useIsNativeEmbed } from '../music24/navigation/hooks/useIsNativeEmbed';
import { useOnboardingState } from './useOnboardingState';
import { readRememberedWelcome, rememberWelcome } from './rememberedWelcome';
import {
  hasWelcomeReturnTarget,
  isEstablishedAccount,
  isWelcomeStartPage,
  saveWelcomeReturnTarget,
  takeWelcomeReturnTarget,
} from './welcomeReturnTarget';

/** The pages that must open without the welcome page in front of them. */
const EXCEPTED_PATHS = /(^|\/)(welcome|oauth-consent|paymentComplete|team\/accept)\/?$/;

export function isWelcomeExcepted(pathname: string): boolean {
  return EXCEPTED_PATHS.test(pathname);
}

interface UseWelcomeRedirectOptions {
  /** `false` in public mode, or when nobody is signed in. */
  enabled: boolean;
}

/** The longest time the gate holds the page while the first answer loads (Ruling R19). */
export const WELCOME_GATE_MAX_MS = 3000;

export interface WelcomeRedirect {
  /**
   * True while the page must not render: the welcome page is due and the redirect runs, or the
   * first answer for this user loads (at most `WELCOME_GATE_MAX_MS`). A failed or paused (offline)
   * answer never holds the page (fail open), and a known answer keeps the page during a refetch.
   */
  holdPage: boolean;
  /**
   * True while the welcome page is due or its first answer still loads. Other windows (the plan
   * window of a checkout intent) wait for it. A failed or paused answer gives false (fail open).
   */
  welcomePending: boolean;
}

/**
 * Sends a signed-in person who has not seen the welcome page to it. This also covers the people
 * who signed up before the new onboarding. It runs once for each page view. The native apps draw
 * their own welcome, so the native web view never redirects. The tour needs the other pages, so
 * the redirect waits until the tour ends.
 *
 * Deep links (QA 2026-10-24 B02):
 * - An account that already has an artist with a song (or a team member) sees the welcome page on
 *   Home only. A deep link (an email link to the Inbox, a release, `returnTo` of a sign-in) opens.
 * - A new account sees the welcome page first. The asked page is kept, and when the welcome ends on
 *   Home, the app opens the asked page. When the welcome ends on another page (Add my song, the
 *   tour), that choice wins and the kept page is dropped.
 */
export function useWelcomeRedirect({ enabled }: UseWelcomeRedirectOptions): WelcomeRedirect {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const router = useSMLRouter();
  const isNativeEmbed = useIsNativeEmbed();
  const isTourActive = useTourActive();
  const userId = useUserTokenDecoded()?.id ?? null;
  const { state, isError, isPaused } = useOnboardingState();
  const liveShowWelcome = state?.showWelcome;

  useEffect(() => {
    if (liveShowWelcome !== undefined) {
      rememberWelcome(userId, liveShowWelcome);
    }
  }, [liveShowWelcome, userId]);

  // A workspace switch resets the cache: the last known answer of this user keeps the page.
  const showWelcome = liveShowWelcome ?? readRememberedWelcome(userId);
  // A remembered `true` decides only on a start page. On a deep link the gate waits for the live
  // answer, because an old account opens a deep link at once.
  const waitsForLive =
    liveShowWelcome === undefined && showWelcome === true && !isWelcomeStartPage(pathname) && !isError && !isPaused;
  const isUnknown = (showWelcome === undefined && !isError && !isPaused) || waitsForLive;
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    if (!isUnknown) {
      return undefined;
    }
    const timer = setTimeout(() => setTimedOut(true), WELCOME_GATE_MAX_MS);
    return () => clearTimeout(timer);
  }, [isUnknown]);

  const { replace } = router;
  const applies = enabled && !isNativeEmbed && !isTourActive && !isWelcomeExcepted(pathname);
  // A known old account opens a deep link; only a start page shows its welcome page.
  const opensDeepLink = state !== undefined && isEstablishedAccount(state) && !isWelcomeStartPage(pathname);
  const isDue = showWelcome === true && !opensDeepLink && !waitsForLive;

  useEffect(() => {
    if (!applies || !isDue) {
      return;
    }
    saveWelcomeReturnTarget(userId, pathname, search);
    replace(SMLRoutes.welcome);
    // The effect runs again for a new page (`pathname`), not for each state refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applies, pathname, isDue]);

  // The welcome is done: the first page after it decides what happens to the kept page.
  const isDone = showWelcome === false;
  useEffect(() => {
    if (!applies || !isDone || !hasWelcomeReturnTarget()) {
      return;
    }
    const target = takeWelcomeReturnTarget(userId);
    if (target !== null && isWelcomeStartPage(pathname)) {
      Promise.resolve(navigate(`/${target}`, { replace: true })).catch(console.error);
    }
  }, [applies, isDone, navigate, pathname, userId]);

  return {
    holdPage: applies && (isDue || (isUnknown && !timedOut)),
    welcomePending: enabled && (isDue || isUnknown),
  };
}
